import type { ImageRange, ImageResolution } from '@/app/types';

export const TIME_OUT = 100;

export const getNewRange = (originalRange: ImageRange, targetRatio: number, maxImageRange: ImageRange): ImageRange => {
  const [currentLeft, currentTop, currentRight, currentBottom] = originalRange;
  const currentWidth = currentRight - currentLeft;
  const currentHeight = currentBottom - currentTop;
  const currentCenterX = (currentLeft + currentRight) / 2;
  const currentCenterY = (currentTop + currentBottom) / 2;

  // 计算新的裁剪区域尺寸，保持短边长度不变
  const currentShortSide = Math.min(currentWidth, currentHeight);

  let newWidth: number;
  let newHeight: number;

  if (targetRatio > 1) {
    // 新比例是横向的，高度为短边
    newHeight = currentShortSide;
    newWidth = newHeight * targetRatio;
  } else {
    // 新比例是纵向的，宽度为短边
    newWidth = currentShortSide;
    newHeight = newWidth / targetRatio;
  }

  // 以当前中心点为基准计算新的裁剪区域
  let newLeft = currentCenterX - newWidth / 2;
  let newTop = currentCenterY - newHeight / 2;
  let newRight = currentCenterX + newWidth / 2;
  let newBottom = currentCenterY + newHeight / 2;

  // 边界检查和调整，确保不超出图片范围
  const [imgLeft, imgTop, imgRight, imgBottom] = maxImageRange;

  // 如果超出左边界
  if (newLeft < imgLeft) {
    newLeft = imgLeft;
    newRight = newLeft + newWidth;
    // 如果调整后右边界也超出，需要缩放
    if (newRight > imgRight) {
      newRight = imgRight;
      newWidth = newRight - newLeft;
      newHeight = newWidth / targetRatio;
      newTop = currentCenterY - newHeight / 2;
      newBottom = currentCenterY + newHeight / 2;
    }
  }

  // 如果超出右边界
  if (newRight > imgRight) {
    newRight = imgRight;
    newLeft = newRight - newWidth;
    // 如果调整后左边界也超出，需要缩放
    if (newLeft < imgLeft) {
      newLeft = imgLeft;
      newWidth = newRight - newLeft;
      newHeight = newWidth / targetRatio;
      newTop = currentCenterY - newHeight / 2;
      newBottom = currentCenterY + newHeight / 2;
    }
  }

  // 如果超出上边界
  if (newTop < imgTop) {
    newTop = imgTop;
    newBottom = newTop + newHeight;
    // 如果调整后下边界也超出，需要缩放
    if (newBottom > imgBottom) {
      newBottom = imgBottom;
      newHeight = newBottom - newTop;
      newWidth = newHeight * targetRatio;
      newLeft = currentCenterX - newWidth / 2;
      newRight = currentCenterX + newWidth / 2;
    }
  }

  // 如果超出下边界
  if (newBottom > imgBottom) {
    newBottom = imgBottom;
    newTop = newBottom - newHeight;
    // 如果调整后上边界也超出，需要缩放
    if (newTop < imgTop) {
      newTop = imgTop;
      newHeight = newBottom - newTop;
      newWidth = newHeight * targetRatio;
      newLeft = currentCenterX - newWidth / 2;
      newRight = currentCenterX + newWidth / 2;
    }
  }

  // 最终边界检查，确保所有坐标都在有效范围内
  newLeft = Math.max(newLeft, imgLeft);
  newTop = Math.max(newTop, imgTop);
  newRight = Math.min(newRight, imgRight);
  newBottom = Math.min(newBottom, imgBottom);

  return [newLeft, newTop, newRight, newBottom];
};

export const calculateRangeAndScale = (fileResolution: ImageResolution, domResolution: ImageResolution) => {
  const { width: fileWidth, height: fileHeight } = fileResolution;
  const { width: domWidth, height: domHeight } = domResolution;
  const fileRatio = fileWidth / fileHeight;
  const domRatio = domWidth / domHeight;
  let scale = domWidth / fileWidth; // 默认文件宽高比和dom宽高比相同
  const range: [number, number, number, number] = [0, 0, domWidth, domHeight];
  if (domRatio > fileRatio) {
    scale = domHeight / fileHeight;
    range[0] = (domWidth - fileWidth * scale) / 2;
    range[1] = 0;
    range[2] = range[0] + fileWidth * scale;
    range[3] = fileHeight * scale;
  } else if (domRatio < fileRatio) {
    scale = domWidth / fileWidth;
    range[0] = 0;
    range[1] = (domHeight - fileHeight * scale) / 2;
    range[2] = fileWidth * scale;
    range[3] = range[1] + fileHeight * scale;
  }
  return {
    range,
    scale,
  };
};

/**
 * 将基于 DOM 显示坐标的裁剪范围转换为原图的百分比范围（0~1）。
 *
 * 注意：displayRange 虽然是基于 domResolution 的坐标，但不能直接用 domResolution 计算百分比，
 * 因为图片在 DOM 容器中是居中显示的（类似 object-fit: contain），
 * 需要通过 fileResolution 计算出图片的实际显示位置和缩放比例。
 */
export const displayRangeToPercent = (
  displayRange: ImageRange,
  fileResolution: ImageResolution,
  domResolution: ImageResolution,
): { x: number; y: number; width: number; height: number } => {
  // 防止除以0
  if (fileResolution.width === 0 || fileResolution.height === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const { scale, range: imageRange } = calculateRangeAndScale(fileResolution, domResolution);
  const [dx1, dy1, dx2, dy2] = displayRange;

  // 图片在 DOM 中的实际显示尺寸
  const displayedWidth = fileResolution.width * scale;
  const displayedHeight = fileResolution.height * scale;

  // 计算裁剪区域在图片中的百分比并限制在 [0, 1] 范围内
  const x = Math.max(0, Math.min(1, (dx1 - imageRange[0]) / displayedWidth));
  const y = Math.max(0, Math.min(1, (dy1 - imageRange[1]) / displayedHeight));
  const width = Math.max(0, Math.min(1 - x, (dx2 - dx1) / displayedWidth));
  const height = Math.max(0, Math.min(1 - y, (dy2 - dy1) / displayedHeight));

  return { x, y, width, height };
};
